import './style.css';

import { useEffect, useState, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWopiToken } from '@/shared/hooks/useWopiToken.js';
import { originOf } from '@/shared/utils/collaboraOrigin.js';
import { ASSISTANT_TOGGLE } from '@/shared/utils/aipPanel';
import { EditorNoAccess } from './EditorNoAccess.jsx';

/**
 * CollaboraEditor - Embeds Collabora Online editor via iframe.
 *
 * Uses the WOPI protocol to give Collabora access to documents.
 * Communicates with Collabora via the PostMessage API.
 *
 * @param {string} fileUuid - The file's uuid (with a shareToken: a file inside the linked folder, or none for the linked file)
 * @param {function} onReady - Called when the document is loaded
 * @param {function} onClose - Called when the user closes the editor
 * @param {function} onModified - Called when the document is modified (unsaved changes)
 * @param {function} onSaved - Called when the document is saved
 * @param {string} title - Optional display name for the file (agent dock)
 * @param {function} onLoadError - Called with {status, code} when the WOPI token is refused
 *   (a 403/404 shows "You don't have access to this document yet", never the raw error)
 *
 * The in-editor assistant dock (branding.js) is armed after Document_Loaded
 * for every EDIT session — a file uuid, no share token, no version preview,
 * not read-only — by posting Zehn_AgentContext with the panel URL
 * (`/agent-panel?file=<uuid>&title=&lang=`, this origin's AgentPanelPage).
 * Collabora's own "Assistant" toolbar button (EnableRemoteAIContent) then
 * toggles the dock; panel events relayed up by branding.js are handled here.
 */
export const CollaboraEditor = memo(function CollaboraEditor({
  fileUuid,
  readonly = false,
  shareToken = null,
  versionNumber = null,
  onReady,
  onClose,
  onModified,
  onSaved,
  title = null,
  onLoadError,
}) {
  const { t, i18n } = useTranslation('editor');
  const iframeRef = useRef(null);
  const formRef = useRef(null);
  const iframeTimeoutRef = useRef(null);
  // The document the form loaded (Collabora host, path, WOPISrc); a token
  // renewed for the same one is handed over in place.
  const sessionRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState(null);
  const onReadyRef = useRef(onReady);
  const onCloseRef = useRef(onClose);
  const onModifiedRef = useRef(onModified);
  const onSavedRef = useRef(onSaved);
  const onLoadErrorRef = useRef(onLoadError);
  // Inputs of the dock arm rule (and the app language), mirrored into a ref
  // so the message handler (bound once per collaboraOrigin) always sees the
  // current session, and a language change never reloads the editor.
  const language = i18n.language;
  const armRef = useRef({ fileUuid, shareToken, versionNumber, readonly, title, language });

  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onModifiedRef.current = onModified; }, [onModified]);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);
  useEffect(() => { onLoadErrorRef.current = onLoadError; }, [onLoadError]);
  useEffect(() => {
    armRef.current = { fileUuid, shareToken, versionNumber, readonly, title, language };
  }, [fileUuid, shareToken, versionNumber, readonly, title, language]);

  const { token, tokenTtl, wopiSrc, collaboraUrl, editorPath, loading, error, errorInfo } = useWopiToken(fileUuid, {
    enabled: !!fileUuid || !!shareToken,
    readonly,
    shareToken,
    // Read-only WOPI session serving a historical version's bytes
    version: versionNumber,
  });
  // The frame's origin, from discovery (or VITE_COLLABORA_URL). Every message
  // in either direction is pinned to it; nothing is accepted or sent before
  // it is known.
  const collaboraOrigin = originOf(collaboraUrl);

  useEffect(() => {
    if (errorInfo) onLoadErrorRef.current?.(errorInfo);
  }, [errorInfo]);

  // Handle PostMessage events from Collabora
  useEffect(() => {
    const handler = (event) => {
      // Our editor frame (not another window of Collabora's origin, such as
      // a second editor on the page) and its exact origin: a prefix test let
      // look-alike hosts through.
      if (!collaboraOrigin || event.origin !== collaboraOrigin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;

      const data = typeof event.data === 'string' ? (() => {
        try { return JSON.parse(event.data); } catch { return {}; }
      })() : event.data;

      if (!data || !data.MessageId) return;

      switch (data.MessageId) {
        case 'App_LoadingStatus':
          if (data.Values?.Status === 'Frame_Ready') {
            // Tell Collabora we're ready to receive messages
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ MessageId: 'Host_PostmessageReady' }),
              collaboraOrigin
            );
          }
          if (data.Values?.Status === 'Document_Loaded') {
            if (iframeTimeoutRef.current) {
              clearTimeout(iframeTimeoutRef.current);
              iframeTimeoutRef.current = null;
            }
            setStatus('ready');
            onReadyRef.current?.();
            // Arm the in-editor assistant dock (branding.js) for every edit
            // session: the panel iframe URL rides down in one Zehn_AgentContext
            // envelope. Hidden until the Assistant toolbar button toggles it.
            // Guest-share and version-preview sessions never arm (the dock
            // page reads with this origin's session); read-only sessions
            // don't either — the assistant edits in place.
            const arm = armRef.current;
            const armed = Boolean(arm.fileUuid) && !arm.shareToken && arm.versionNumber == null && !arm.readonly;
            if (armed) {
              const panelUrl = `${window.location.origin}/agent-panel`
                + `?file=${encodeURIComponent(arm.fileUuid)}`
                + `&title=${encodeURIComponent(arm.title || '')}`
                // Same partition as this frame, but pass it explicitly so the
                // panel can never boot in a different language than the app.
                + `&lang=${encodeURIComponent(arm.language)}`;
              iframeRef.current?.contentWindow?.postMessage(
                JSON.stringify({
                  MessageId: 'Zehn_AgentContext',
                  SendTime: Date.now(),
                  Values: {
                    panelUrl,
                    fileUuid: arm.fileUuid,
                    title: arm.title || '',
                    // Open by default in edit sessions (user decision 2026-09-08); the
                    // dock's X hides it for the rest of the session.
                    visible: true,
                  },
                }),
                collaboraOrigin
              );
            }
          }
          break;

        case 'Doc_ModifiedStatus':
          onModifiedRef.current?.(data.Values?.Modified === true);
          break;

        case 'Action_Save_Resp':
          if (data.Values?.success) {
            onSavedRef.current?.();
          }
          break;

        case 'UI_Close':
          onCloseRef.current?.();
          break;

        // Upstream "Assistant" toolbar button (shown when CheckFileInfo sets
        // EnableRemoteAIContent) — map it to showing/hiding the agent dock.
        case 'UI_InsertAIContent':
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({
              MessageId: 'Zehn_AgentPanel_Toggle',
              SendTime: Date.now(),
              Values: {},
            }),
            collaboraOrigin
          );
          break;

        // Agent panel events relayed up by branding.js (verbatim envelope).
        case 'Zehn_PanelEvent': {
          const values = data.Values || {};
          if (values.type === 'close_panel') {
            // The dock can only hide itself through the editor frame.
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({
                MessageId: 'Zehn_AgentPanel_Toggle',
                SendTime: Date.now(),
                Values: { visible: false },
              }),
              collaboraOrigin
            );
          } else if (values.type === 'document_changed') {
            // An in-place agent edit was applied and saved from inside the
            // editor frame — no Action_Save_Resp reaches us for that save.
            onSavedRef.current?.();
          }
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [collaboraOrigin]);

  // The header's Assistant button (aipPanel.toggleAssistant): shows or hides
  // the dock — only an edit session has one (see the arming above).
  useEffect(() => {
    const toggle = () => {
      const arm = armRef.current;
      if (!collaboraOrigin || !arm.fileUuid || arm.shareToken || arm.versionNumber != null || arm.readonly) return;
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ MessageId: 'Zehn_AgentPanel_Toggle', SendTime: Date.now(), Values: {} }),
        collaboraOrigin
      );
    };
    window.addEventListener(ASSISTANT_TOGGLE, toggle);
    return () => window.removeEventListener(ASSISTANT_TOGGLE, toggle);
  }, [collaboraOrigin]);

  // Load the editor when token is ready
  useEffect(() => {
    if (!token || !wopiSrc || !collaboraUrl) return;

    // WOPI spec: access_token_ttl is the absolute expiry time in ms since epoch (NOT a duration).
    // 0 means "no expiry"; we use the real expiry so Collabora's warning fires accurately.
    const expiryMs = tokenTtl ? Date.now() + tokenTtl * 1000 : 0;

    // A renewed token (they live an hour at most) for the document already
    // open: hand it to the editor in place — Collabora's Reset_Access_Token —
    // rather than posting the form again, which would reload the editor.
    const session = `${collaboraUrl}|${editorPath || ''}|${wopiSrc}`;
    if (sessionRef.current === session) {
      if (collaboraOrigin) {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({
            MessageId: 'Reset_Access_Token',
            SendTime: Date.now(),
            Values: { token, ttl: String(expiryMs) },
          }),
          collaboraOrigin
        );
      }
      return undefined;
    }
    sessionRef.current = session;

    // Build the Collabora editor URL using the path from discovery (includes version hash)
    const coolPath = editorPath || '/browser/dist/cool.html';
    // Editor chrome follows the app language (Collabora wants full locales).
    // No Cyrillic Uzbek chrome upstream — both scripts get Collabora's `uz`.
    const collaboraLang = { uz: 'uz', 'uz-Cyrl': 'uz', ru: 'ru-RU', en: 'en-US' }[armRef.current.language] || 'en-US';
    const editorUrl = `${collaboraUrl}${coolPath}?WOPISrc=${encodeURIComponent(wopiSrc)}&lang=${collaboraLang}`;

    // Use form POST to load the iframe (as recommended by WOPI spec)
    if (formRef.current && iframeRef.current) {
      formRef.current.action = editorUrl;
      formRef.current.querySelector('input[name="access_token"]').value = token;
      formRef.current.querySelector('input[name="access_token_ttl"]').value = String(expiryMs);
      formRef.current.submit();

      // Timeout: if Collabora doesn't load within 20s, show error
      if (iframeTimeoutRef.current) clearTimeout(iframeTimeoutRef.current);
      iframeTimeoutRef.current = setTimeout(() => {
        setStatus('error');
        setErrorMsg('Document editor failed to load. Collabora Online may not be running.');
      }, 20000);
    }

    return () => {
      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
        iframeTimeoutRef.current = null;
      }
    };
  }, [token, tokenTtl, wopiSrc, collaboraUrl, editorPath, collaboraOrigin]);

  // Public methods via imperative handle (the modals' Save / Print go through
  // these, so they need not know the origin). No-op until the origin is known.
  const sendCommand = useCallback((command) => {
    if (!collaboraOrigin) return;
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify(command),
      collaboraOrigin
    );
  }, [collaboraOrigin]);

  const save = useCallback(() => {
    sendCommand({ MessageId: 'Action_Save', Values: { DontTerminateEdit: true, DontSaveIfUnmodified: false } });
  }, [sendCommand]);

  // Expose methods
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current._collabora = { save, sendCommand };
    }
  }, [save, sendCommand]);

  if (errorInfo && (errorInfo.status === 403 || errorInfo.status === 404)) {
    return <EditorNoAccess reason={errorInfo.code} onRetry={() => window.location.reload()} />;
  }

  if (error || status === 'error') {
    // A refused token names its reason by code; a fault keeps its own text.
    const detail = errorInfo?.code ? t(`errors:${errorInfo.code}`, { defaultValue: error }) : error;
    return (
      <div className="collabora-error">
        <p>{t('loadFailed')}: {detail || errorMsg || t('unknownError')}</p>
        <button onClick={() => window.location.reload()}>{t('retry')}</button>
      </div>
    );
  }

  return (
    <div className="collabora-container">
      {(loading || status === 'loading') && (
        <div className="collabora-loading">
          <div className="collabora-spinner" />
          <span>{t('loading')}</span>
        </div>
      )}

      {/* Hidden form for WOPI token POST */}
      <form
        ref={formRef}
        target="collabora-frame"
        method="POST"
        style={{ display: 'none' }}
      >
        <input name="access_token" type="hidden" value="" />
        <input name="access_token_ttl" type="hidden" value="" />
      </form>

      {/* No sandbox: Chrome blocks PDF blobs inside sandboxed iframes, which
          breaks Collabora's print (it renders to a PDF blob and prints it).
          sandbox with allow-scripts+allow-same-origin adds no real isolation
          anyway, and Collabora is our own trusted service. */}
      <iframe
        ref={iframeRef}
        name="collabora-frame"
        className="collabora-frame"
        title="Document Editor"
        allowFullScreen
      />
    </div>
  );
});
